<template>
  <vue-draggable-resizable
    :active="true"
    :w="width"
    :h="height"
    :x="posX"
    :y="posY"
    @dragstop="onDragStop"
    @resizing="onResizing"
    :parent="true"
    :resizable="true"
    class-name-handle="object-form-resizable-handle"
    class="home-form-container"
    :drag-handle="'.drag'"
  >
    <div class="form" ref="detailForm">
      <form-header :title="pageTitle" @close="close" />

      <div class="form-nav">
        <div
          @click="activeTab = index"
          class="form-nav-tab"
          :class="{ active: activeTab === index }"
          v-for="(tab, index) in tabs"
          v-if="tab.show"
          :key="tab.label"
        >
          {{ $tc(tab.label, 1) }}
        </div>
      </div>

      <div class="tableContainer">
        <detail-data-table
          :original-data="tabs[activeTab].data"
          :max-height="height"
          @resize-tab="resizeTab"
        />
      </div>

      <div class="saveDiv" />
    </div>
  </vue-draggable-resizable>
</template>

<script>
import DetailDataTable from "../_Molecules/DetailDataTable";
import { bus } from "@/main";
import WindowHeaderButton from "../../../_Atoms/WindowHeaderButton";
import FormHeader from "@/components/Home/Forms/_Molecules/FormHeader";

export default {
  name: "DetailForm",

  components: { FormHeader, WindowHeaderButton, DetailDataTable },

  props: {
    dataReceived: {
      type: Object,
      required: true
    },

    actionType: {
      type: String
    },

    wkt: {},

    streetView: { type: Boolean },

    previousPosX: {
      type: Number
    },

    previousPosY: {
      type: Number
    }
  },

  data() {
    return {
      posY: 100,
      posX: 400,
      width: 550,
      height: 400,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      tabs: [
        {
          label: "MainData",
          show: true,
          data: {}
        },
        {
          label: "CustomData",
          show: false,
          data: {}
        },
        {
          label: "TechnicalSite",
          show: false,
          data: {}
        },
        {
          label: "TechnicalPoint",
          show: false,
          data: {}
        },
        {
          label: "Node",
          show: false,
          data: {}
        }
      ],
      numberOfTabs: undefined,
      activeTab: 0
    };
  },

  watch: {
    streetView() {
      this.setInitialPosition(this.$refs.detailForm);
    }
  },

  mounted() {
    this.distributeData();

    let element = this.$refs.detailForm;
    this.$nextTick(() => {
      /* Sets the initial position of the form (centers it) */
      this.setInitialPosition();
    });
  },

  computed: {
    pageTitle() {
      let prefix = Object.keys(this.dataReceived)[0].split('_')[0]
      let objectLabel = prefix + '_label';
      let objectId = prefix + '_id';
      let objectIdentifier = this.dataReceived[objectLabel] ? this.dataReceived[objectLabel] : this.dataReceived[objectId];
      return this.$i18n.tc('Detail', 2) + ' - ' + objectIdentifier;
    }
  },

  methods: {
    /**
     * Defines the initial position of the current component.
     */
    setInitialPosition() {
      this.posX = Math.round((this.screenWidth/ 2) - 550/2);
      this.posY = Math.round((this.screenHeight/ 2) - 400/2);
      bus.$emit("update-object-form-position", { x: this.posX, y: this.posY });
    },

    onDragStop(x, y) {
      bus.$emit("update-object-form-position", { x: x, y: y });
      this.posX = x;
      this.posY = y;
    },

    onResizing(left, top, width, height) {
      this.height = height;
      this.width = width;
    },

    /**
     * Sets the initials size of the Details form.
     * @param element
     */
    setSize(element) {
      let formWidth = element.offsetWidth;
      let formHeight = element.offsetHeight;
      this.width = formWidth;
      this.height = formHeight;
    },

    /**
     * Closes de Details Form
     */
    close() {
      this.$emit("close", this);
    },

    /**
     * Distributes the received data (especially the contained objects) into several tab.s
     */
    distributeData() {
      this.tabs[0].data = this.dataReceived;

      this.prepareTabData("custom_data", 1, 0);
      this.prepareTabData("technicalsite", 2, 0).then(() =>
        this.prepareTabData("node", 4, 2)
      );
      this.prepareTabData("technicalpoint", 3, 0).then(() =>
        this.prepareTabData("node", 4, 3)
      );
      this.prepareTabData("node", 4, 0);
    },

    /**
     * Checks if a key actually exists and if it has a value to be shown.
     * @param {String} key - name of the key
     * @param {Object} originData - data being searched
     * @returns {boolean}
     */
    keyHasValue(key, originData) {
      return key && originData.hasOwnProperty(key) && originData[key] !== null;
    },

    /**
     * Prepares the object correspondent to a given key to be shown in a dedicated tab.
     * @param {String} dataKey - name of the key holding the data
     * @param {Number} tabIndex - index of the destination tab for the data
     * @param {Number} tabOfOriginIndex - index of the origin tab of the data
     * @returns {Promise<unknown>}
     */
    prepareTabData(dataKey, tabIndex, tabOfOriginIndex) {
      return new Promise(resolve => {
        let originData = this.tabs[tabOfOriginIndex].data;
        if (this.keyHasValue(dataKey, originData)) {
          this.tabs[tabIndex].show = true;
          let data = originData[dataKey];
          if (typeof data === "string") {
            data = JSON.parse(data);
          }
          this.tabs[tabIndex].data = data;
          delete this.tabs[tabOfOriginIndex].data[dataKey];
          resolve();
        }
      });
    },

    resizeTab() {
      let element = this.$refs.detailForm;

      let contentHeight = this.$parent.$refs.formsContainer.offsetHeight;
      let formHeight = element.offsetHeight;
      let height = contentHeight - formHeight;

      if (this.posY > height) {
        this.posY = Math.round(height);
      }
      this.setSize(element);
    }
  }
};
</script>

<style scoped>
.home-form-container {
  min-width: 500px !important;
  z-index: 1000 !important;
  height: auto;
  transition: opacity 3s;
}
.form-nav{
  height: 30px;
}
.saveDiv{
  height: 10px;
}
.tableContainer{
  height: calc(100% - 80px);
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/ObjectForms/DetailForm.vue